import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { dateTime, money, titleCase } from "../lib/format";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Layout";
import {
  Button,
  Card,
  DataState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Notice,
  Select,
  StatusBadge,
  Table,
} from "../components/ui";
import type { EmploymentType, Membership, PayType, Role } from "../lib/types";

type SettingsPayload = {
  organization: {
    id: string;
    name: string;
    legalName: string | null;
    timezone: string;
    taxRateBps: number;
    taxesParts: boolean;
    taxesLabor: boolean;
    taxesFees: boolean;
    paymentProvider: string;
  };
  locations: Array<{ id: string; name: string; code: string; city: string | null }>;
  laborRates: Array<{ id: string; name: string; ratePerHour: number; costPerHour: number; isDefault: boolean }>;
  suppliers: Array<{ id: string; name: string; accountNumber: string | null }>;
  providerConnections: Array<{
    id: string;
    kind: string;
    provider: string;
    externalAccountId: string | null;
    isLive: boolean;
    isEnabled: boolean;
    hasCredentials: boolean;
  }>;
  implementedPaymentProviders: Array<{ name: string; capabilities: Record<string, unknown> }>;
};

export function SettingsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsPayload>("/settings"),
  });

  const staff = useQuery({
    queryKey: ["staff"],
    queryFn: () => api.get<{ data: Membership[] }>("/settings/staff"),
  });

  const audit = useQuery({
    queryKey: ["audit"],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          action: string;
          resourceType: string;
          reason: string | null;
          createdAt: string;
          actor: { firstName: string; lastName: string } | null;
        }>;
      }>("/settings/audit"),
    enabled: can("audit.read"),
  });

  const [tax, setTax] = useState<string | null>(null);
  const [addingStaff, setAddingStaff] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Membership | null>(null);

  const saveTax = useMutation({
    mutationFn: (bps: number) => api.patch("/settings/organization", { taxRateBps: bps }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      setTax(null);
    },
  });

  const org = settings.data?.organization;

  return (
    <>
      <PageHeader
        title="Settings"
        description="Shop configuration, staff, rates and integrations."
      />

      <DataState loading={settings.isLoading} error={settings.error}>
        {settings.data && org && (
          <div className="space-y-5">
            <Card title="Tax configuration">
              <Notice tone="warn">
                Sales tax is tracked as a liability, never as revenue. The rate and what it applies
                to must be reviewed for your launch jurisdiction by a qualified adviser before you
                go live.
              </Notice>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field
                  label="Sales tax rate"
                  hint="Stored in basis points. 950 is 9.50%."
                >
                  <div className="flex gap-2">
                    <Input
                      value={tax ?? String(org.taxRateBps)}
                      onChange={(e) => setTax(e.target.value)}
                      inputMode="numeric"
                      disabled={!can("settings.write")}
                      className="tabular-nums"
                    />
                    {can("settings.write") && tax !== null && (
                      <Button
                        loading={saveTax.isPending}
                        onClick={() => saveTax.mutate(Number(tax))}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                </Field>

                <div className="space-y-1 text-sm">
                  <p className="font-medium text-ink">Applies to</p>
                  <p className="text-slate-600">
                    Parts: {org.taxesParts ? "taxed" : "not taxed"}
                  </p>
                  <p className="text-slate-600">
                    Labor: {org.taxesLabor ? "taxed" : "not taxed"}
                  </p>
                  <p className="text-slate-600">Fees: {org.taxesFees ? "taxed" : "not taxed"}</p>
                </div>
              </div>
              <ErrorBanner error={saveTax.error} />
            </Card>

            <Card title="Payments">
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded border border-slate-200 p-3">
                  <div>
                    <p className="font-medium text-ink">
                      Selected provider: {org.paymentProvider}
                    </p>
                    <p className="text-xs text-slate-500">
                      Only providers with a working adapter can be selected. An unimplemented
                      provider is never offered.
                    </p>
                  </div>
                  <StatusBadge
                    status={
                      settings.data.implementedPaymentProviders.some(
                        (p) => p.name === org.paymentProvider,
                      )
                        ? "SUCCEEDED"
                        : "FAILED"
                    }
                  />
                </div>

                <Table headers={["Kind", "Provider", "Merchant account", "Mode", "Credentials"]}>
                  {settings.data.providerConnections.map((c) => (
                    <tr key={c.id}>
                      <td className="px-3 py-2 text-xs text-slate-500">{titleCase(c.kind)}</td>
                      <td className="px-3 py-2 font-medium">{c.provider}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        {c.externalAccountId ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                            c.isLive ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {c.isLive ? "live" : "sandbox"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {/* Secrets stay in the secret store; only status is shown. */}
                        {c.hasCredentials ? "stored in secret store" : "not configured"}
                      </td>
                    </tr>
                  ))}
                </Table>
              </div>
            </Card>

            <Card title="Labor rates">
              <Table headers={["Name", "Charged per hour", "Cost per hour", "Default"]}>
                {settings.data.laborRates.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 tabular-nums">{money(r.ratePerHour)}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">
                      {can("reports.costs") ? money(r.costPerHour) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.isDefault ? "Yes" : ""}</td>
                  </tr>
                ))}
              </Table>
            </Card>

            <Card
              title="Team"
              action={
                can("staff.manage") ? (
                  <Button variant="secondary" onClick={() => setAddingStaff(true)}>
                    Add staff
                  </Button>
                ) : undefined
              }
            >
              {can("payroll.read") && (
                <div className="mb-3">
                  <Notice>
                    Pay rate is visible here and editable only by an owner. It is not shown to
                    managers, even though a manager can see shop-wide job margins.
                  </Notice>
                </div>
              )}
              <DataState loading={staff.isLoading} error={staff.error}>
                <Table
                  headers={
                    can("payroll.read")
                      ? ["Name", "Email", "Role", "Location", "Employment", "Pay", "Last sign-in", "Status", ""]
                      : ["Name", "Email", "Role", "Location", "Last sign-in", "Status", ""]
                  }
                >
                  {staff.data?.data.map((m) => (
                    <tr key={m.id} className={m.isActive ? "" : "opacity-50"}>
                      <td className="px-3 py-2 font-medium">
                        {m.user.firstName} {m.user.lastName}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{m.user.email}</td>
                      <td className="px-3 py-2">{titleCase(m.role)}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {m.location?.name ?? "All locations"}
                      </td>
                      {can("payroll.read") && (
                        <>
                          <td className="px-3 py-2 text-xs text-slate-500">
                            {m.employmentType ? titleCase(m.employmentType) : "—"}
                          </td>
                          <td className="px-3 py-2 tabular-nums text-xs text-slate-600">
                            {m.payRateCents === null
                              ? "—"
                              : m.payType === "HOURLY"
                                ? `${money(m.payRateCents)}/hr`
                                : `${money(m.payRateCents)}/period`}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {dateTime(m.user.lastLoginAt)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {m.isActive ? "Active" : "Access revoked"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {can("staff.manage") && m.isActive && (
                          <Button variant="secondary" onClick={() => setEditingStaff(m)}>
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              </DataState>
            </Card>

            {can("audit.read") && (
              <Card title="Audit trail">
                <DataState
                  loading={audit.isLoading}
                  error={audit.error}
                  empty={audit.data?.data.length === 0}
                  emptyMessage="No audit events yet."
                >
                  <Table headers={["When", "Who", "Action", "Resource", "Reason"]}>
                    {audit.data?.data.slice(0, 50).map((e) => (
                      <tr key={e.id}>
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                          {dateTime(e.createdAt)}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {e.actor ? `${e.actor.firstName} ${e.actor.lastName}` : "System / customer"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{e.action}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{e.resourceType}</td>
                        <td className="max-w-xs truncate px-3 py-2 text-xs text-slate-600">
                          {e.reason ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </Table>
                </DataState>
              </Card>
            )}
          </div>
        )}
      </DataState>

      {addingStaff && (
        <AddStaffModal
          locations={settings.data?.locations ?? []}
          onClose={() => setAddingStaff(false)}
        />
      )}
      {editingStaff && (
        <EditStaffModal
          membership={editingStaff}
          locations={settings.data?.locations ?? []}
          onClose={() => setEditingStaff(null)}
        />
      )}
    </>
  );
}

const ROLE_OPTIONS: Role[] = ["OWNER", "MANAGER", "SERVICE_ADVISOR", "TECHNICIAN", "BOOKKEEPER"];

function EmploymentFields({
  employmentType,
  setEmploymentType,
  payType,
  setPayType,
  payRate,
  setPayRate,
}: {
  employmentType: EmploymentType | "";
  setEmploymentType: (v: EmploymentType | "") => void;
  payType: PayType | "";
  setPayType: (v: PayType | "") => void;
  payRate: string;
  setPayRate: (v: string) => void;
}) {
  return (
    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Employment and pay
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Employment type">
          <Select
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as EmploymentType | "")}
          >
            <option value="">Not set</option>
            <option value="FULL_TIME">Full time</option>
            <option value="PART_TIME">Part time</option>
            <option value="CONTRACTOR">Contractor</option>
          </Select>
        </Field>
        <Field label="Pay type">
          <Select value={payType} onChange={(e) => setPayType(e.target.value as PayType | "")}>
            <option value="">Not set</option>
            <option value="HOURLY">Hourly</option>
            <option value="SALARY">Per pay period</option>
          </Select>
        </Field>
        <Field label={payType === "SALARY" ? "Amount per period" : "Rate per hour"}>
          <Input
            value={payRate}
            onChange={(e) => setPayRate(e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
          />
        </Field>
      </div>
    </div>
  );
}

function AddStaffModal({
  locations,
  onClose,
}: {
  locations: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "SERVICE_ADVISOR" as Role,
    locationId: "",
  });
  const [employmentType, setEmploymentType] = useState<EmploymentType | "">("");
  const [payType, setPayType] = useState<PayType | "">("");
  const [payRate, setPayRate] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.post("/settings/staff", {
        ...form,
        locationId: form.locationId || null,
        ...(can("payroll.write")
          ? {
              employmentType: employmentType || null,
              payType: payType || null,
              payRate: payRate ? Number(payRate) : null,
            }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
  });

  return (
    <Modal title="Add staff" onClose={onClose} wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <ErrorBanner error={create.error} />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" required>
            <Input
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              required
              autoFocus
            />
          </Field>
          <Field label="Last name" required>
            <Input
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              required
            />
          </Field>
        </div>

        <Field label="Email" required hint="If this email already has an account, they'll be added to this shop with a new sign-in.">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
        </Field>

        <Field label="Temporary password" required hint="At least 10 characters. Share it with them directly.">
          <Input
            type="text"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            required
            minLength={10}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Role" required>
            <Select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {titleCase(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Location" hint="Leave as all locations for multi-location access.">
            <Select
              value={form.locationId}
              onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {can("payroll.write") && (
          <EmploymentFields
            employmentType={employmentType}
            setEmploymentType={setEmploymentType}
            payType={payType}
            setPayType={setPayType}
            payRate={payRate}
            setPayRate={setPayRate}
          />
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Add to team
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditStaffModal({
  membership,
  locations,
  onClose,
}: {
  membership: Membership;
  locations: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<Role>(membership.role);
  const [locationId, setLocationId] = useState(membership.locationId ?? "");
  const [employmentType, setEmploymentType] = useState<EmploymentType | "">(
    membership.employmentType ?? "",
  );
  const [payType, setPayType] = useState<PayType | "">(membership.payType ?? "");
  const [payRate, setPayRate] = useState(
    membership.payRateCents !== null ? (membership.payRateCents / 100).toFixed(2) : "",
  );

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/settings/staff/${membership.id}`, {
        role,
        locationId: locationId || null,
        ...(can("payroll.write")
          ? {
              employmentType: employmentType || null,
              payType: payType || null,
              payRate: payRate ? Number(payRate) : null,
            }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
  });

  const deactivate = useMutation({
    mutationFn: () => api.post(`/settings/staff/${membership.id}/deactivate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
  });

  return (
    <Modal title={`Edit ${membership.user.firstName} ${membership.user.lastName}`} onClose={onClose} wide>
      <div className="space-y-4">
        <ErrorBanner error={save.error || deactivate.error} />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {titleCase(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Location">
            <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {can("payroll.write") ? (
          <EmploymentFields
            employmentType={employmentType}
            setEmploymentType={setEmploymentType}
            payType={payType}
            setPayType={setPayType}
            payRate={payRate}
            setPayRate={setPayRate}
          />
        ) : (
          <Notice>Only an owner can view or change pay details.</Notice>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 pt-4">
          <Button
            type="button"
            variant="danger"
            loading={deactivate.isPending}
            onClick={() => deactivate.mutate()}
          >
            Remove access
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" loading={save.isPending} onClick={() => save.mutate()}>
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
